/**
 * @format
 */
import { inject, Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { EducationPhaseProgress, Plan, StudyRight } from 'common-typescript/types';
import { cloneDeep, round } from 'lodash-es';
import moment from 'moment';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { COMMON_PLAN_SERVICE } from 'sis-components/ajs-upgraded-modules';
import { PlannedStudies } from 'sis-components/applications/planned-studies-by-study-term/planned-studies-by-study-term.component';
import { isCourseUnitAttainment } from 'sis-components/attainment/AttainmentUtil';
import { StudyRightTermRegistrationsEntityService } from 'sis-components/service/study-right-term-registrations-entity.service';
import { StudyTermService } from 'sis-components/service/study-term.service';
import { convertAJSPromiseToNative } from 'sis-components/util/utils';
import { ApplicationAndPlanData } from './types';

@Injectable()
export class StudyRightExtensionApplicationService {
    private readonly legacyPlanService = inject(COMMON_PLAN_SERVICE);
    private readonly studyRightTermRegistrationsEntityService = inject(StudyRightTermRegistrationsEntityService);
    private readonly studyTermService = inject(StudyTermService);

    getApplicationAndPlanData(plan: Plan, studyRight: StudyRight): Observable<ApplicationAndPlanData> {
        return from(
            convertAJSPromiseToNative<ValidatablePlan>(this.legacyPlanService.getValidatablePlan(cloneDeep(plan), false, true)),
        ).pipe(
            switchMap(validatablePlan => {
                const attainments = validatablePlan.getAllCourseUnitAndCustomCourseUnitAttainmentsInPlan();
                const courseUnitAttainmentIds = attainments.filter(isCourseUnitAttainment).map(attainment => attainment.id);
                return combineLatest([
                    of(validatablePlan),
                    this.studyRightTermRegistrationsEntityService.getTermRegistrationCounts(studyRight.id),
                    this.studyTermService.groupAttainedCreditsByStudyTerm(
                        attainments,
                        studyRight.valid?.startDate,
                        moment().toISOString(true),
                        studyRight.termRegistrations,
                    ),
                    from(
                        convertAJSPromiseToNative<{
                            phase1Progress?: EducationPhaseProgress;
                            phase2Progress?: EducationPhaseProgress;
                        }>(this.legacyPlanService.getStudyProgressByEducationPhase(validatablePlan, studyRight)),
                    ),
                    from(
                        convertAJSPromiseToNative<PlannedStudies>(
                            this.legacyPlanService.groupPlannedStudiesByStudyTerm(validatablePlan.plan, moment(), courseUnitAttainmentIds),
                        ),
                    ),
                ]);
            }),
            take(1),
            map(([validatablePlan, termRegistrationCounts, attainedCreditsByStudyTerm, studyProgress, plannedStudies]) => {
                const attainments = validatablePlan.getAllCourseUnitAndCustomCourseUnitAttainmentsInPlan();
                const totalCredits = attainments.reduce((sum, { credits }) => sum + credits, 0);
                const creditsPerTerm =
                    termRegistrationCounts.usedAttendanceTerms > 0
                        ? round(totalCredits / termRegistrationCounts.usedAttendanceTerms, 1)
                        : 0;
                return {
                    automaticallyFilledApplicationFields: {
                        attainmentIds: attainments.map(attainment => attainment.id),
                        educationId: studyRight.educationId,
                        phase1Progress: studyProgress.phase1Progress,
                        phase2Progress: studyProgress.phase2Progress,
                        planId: plan.id,
                        // Add the cleaned plan to the application
                        planSnapshot: validatablePlan.plan,
                        previousExtensions: (studyRight.studyRightExtensions ?? [])
                            .filter(extension => extension.state === 'ACTIVE')
                            .sort((a, b) => (b.grantDate ?? '').localeCompare(a.grantDate ?? '')),
                        studyRightId: studyRight.id,
                        studyRightValidity: studyRight.valid,
                        termRegistrations: studyRight.termRegistrations,
                        termsWithoutRegistration: termRegistrationCounts.termsWithoutRegistration,
                        type: 'StudyRightExtensionApplication',
                        usedAbsenceTerms: termRegistrationCounts.usedAbsenceTerms,
                        usedAttendanceTerms: termRegistrationCounts.usedAttendanceTerms,
                        usedStatutoryAbsenceTerms: termRegistrationCounts.usedStatutoryAbsenceTerms,
                    },
                    attainedCreditsByStudyTerm,
                    creditsPerTerm,
                    plannedStudies,
                };
            }),
        );
    }
}
